/**
 * Which of the three things to tell someone who has just come back from
 * Stripe's hosted onboarding.
 *
 * Kept out of the component so it can be tested: the repo's Jest runs under the
 * `node` environment with no DOM, so component rendering is not exercised here.
 *
 * The distinction that matters: Stripe redirecting back only means the user left
 * its flow, never that payouts work. Someone can return with details still
 * outstanding, and telling them "you're all set" would leave them waiting on
 * money that is never coming. `payouts_enabled` is the only signal that means
 * done; `details_submitted` without it means Stripe is still verifying.
 */
export const PAYOUT_RETURN_STATES = {
  COMPLETE: "complete",
  VERIFYING: "verifying",
  UNFINISHED: "unfinished",
  /** No session, so the status can never load — ask them to sign in. */
  SIGNED_OUT: "signedOut",
  /** Session present, status request still in flight. */
  LOADING: "loading",
};

const COPY = {
  [PAYOUT_RETURN_STATES.COMPLETE]: {
    title: "PAYOUT_SETUP_COMPLETE",
    description: "PAYOUT_SETUP_COMPLETE_DESC",
  },
  [PAYOUT_RETURN_STATES.VERIFYING]: {
    title: "PAYOUT_SETUP_SUBMITTED",
    description: "PAYOUT_SETUP_SUBMITTED_DESC",
  },
  [PAYOUT_RETURN_STATES.UNFINISHED]: {
    title: "PAYOUT_SETUP_UNFINISHED",
    description: "PAYOUT_SETUP_UNFINISHED_DESC",
  },
  [PAYOUT_RETURN_STATES.SIGNED_OUT]: {
    title: "PAYOUT_SETUP_SUBMITTED",
    description: "PAYOUT_SETUP_SIGNED_OUT_DESC",
  },
  [PAYOUT_RETURN_STATES.LOADING]: {
    title: "PAYOUT_SETUP_CHECKING",
    description: "PAYOUT_SETUP_CHECKING_DESC",
  },
};

/**
 * The state to show. Never reports COMPLETE unless payouts are actually on, and
 * never reports UNFINISHED on a guess.
 *
 * "Not yet known" is its own state rather than being folded into UNFINISHED.
 * The status starts null and arrives a round-trip later, so treating absent as
 * unfinished told a fully-onboarded user "a few details are still needed" —
 * with a button that sends them back through setup they had just completed.
 *
 * Signed out is separate again: without a session the status can never load, so
 * waiting is pointless and offering to finish setup is worse than pointless —
 * the request 401s and the interceptor clears storage and bounces them home.
 */
export const resolveReturnState = ({
  payoutsEnabled,
  detailsSubmitted,
  statusLoaded = true,
  signedIn = true,
}) => {
  if (!signedIn) {
    return PAYOUT_RETURN_STATES.SIGNED_OUT;
  }
  if (!statusLoaded) {
    return PAYOUT_RETURN_STATES.LOADING;
  }
  if (payoutsEnabled) {
    return PAYOUT_RETURN_STATES.COMPLETE;
  }
  return detailsSubmitted
    ? PAYOUT_RETURN_STATES.VERIFYING
    : PAYOUT_RETURN_STATES.UNFINISHED;
};

/** i18n keys for a state. */
export const getReturnCopy = (state) => COPY[state];

/**
 * Whether to offer the "finish setup" action. Only when something is genuinely
 * outstanding — offering it to a verified user invites them to redo setup they
 * have already completed.
 */
export const canFinishSetup = (state) =>
  state === PAYOUT_RETURN_STATES.UNFINISHED;

/**
 * Whether to offer a manual re-check. The poll is bounded (~96s), so someone
 * still verifying when it gives up would otherwise have no way to re-check
 * short of reloading the page, with nothing on screen to suggest it.
 */
export const canRefreshStatus = (state) =>
  state === PAYOUT_RETURN_STATES.VERIFYING ||
  state === PAYOUT_RETURN_STATES.UNFINISHED;

/** Signed-out visitors get a sign-in prompt instead of a payout action. */
export const needsSignIn = (state) => state === PAYOUT_RETURN_STATES.SIGNED_OUT;
